import { TellescopeModelFromName, useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import {
  Button,
  cn,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { formatName } from "@tellescope/utils";
import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import type { EnduserRelationship } from "@tellescope/types-models";
import { RELATIONSHIP_TYPES } from "@tellescope/constants";

/**
 * Type-safe conversion from string to EnduserRelationship type.
 * Returns undefined if the string is not a valid relationship type.
 */
const toRelationshipType = (value: string): EnduserRelationship["type"] | undefined => {
  return RELATIONSHIP_TYPES.find((type) => type === value);
};

export interface AddRelatedContactFormProps {
  contact: TellescopeModelFromName<"endusers">;
  onSuccess: () => void;
  onCancel?: () => void;
}

export const AddRelatedContactForm = ({ contact, onSuccess, onCancel }: AddRelatedContactFormProps) => {
  const { t } = useTranslation("contacts");
  const [addFormData, setAddFormData] = useState({
    relationship: "",
    contactSearchQuery: "",
    selectedContactId: "",
  });

  const availableContactsQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  const relatedContactIds = useMemo(() => {
    return contact.relationships?.map((rel) => rel.id) ?? [];
  }, [contact.relationships]);

  const updateRelationshipsMutation = useTellescopeMutation({
    model: "endusers",
    method: "updateOne",

    onSuccess: () => {
      setAddFormData({
        relationship: "",
        contactSearchQuery: "",
        selectedContactId: "",
      });
      onSuccess();
    },
  });

  const handleAddRelatedContact = () => {
    if (!addFormData.selectedContactId || !addFormData.relationship) return;

    const relationshipType = toRelationshipType(addFormData.relationship);
    if (!relationshipType) return;

    const newRelationship: EnduserRelationship = {
      id: addFormData.selectedContactId,
      type: relationshipType,
    };

    // replaceObjectFields: false triggers $addToSet behavior to prevent duplicates
    // Backend automatically creates the bidirectional relationship
    updateRelationshipsMutation.mutate({
      id: contact.id,
      relationships: [newRelationship],
      _options: {
        replaceObjectFields: false,
      },
    });
  };

  const contactsForAdding = useMemo(() => {
    if (!availableContactsQuery.data) return [];
    return availableContactsQuery.data.filter((c) => c.id !== contact.id && !relatedContactIds.includes(c.id));
  }, [availableContactsQuery.data, contact.id, relatedContactIds]);

  const filteredAddContacts = useMemo(() => {
    if (!addFormData.contactSearchQuery) return contactsForAdding;
    const query = addFormData.contactSearchQuery.toLowerCase();
    return contactsForAdding.filter(
      (c) =>
        formatName(c).toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        c.phone?.includes(query)
    );
  }, [contactsForAdding, addFormData.contactSearchQuery]);

  const canAddContact = Boolean(addFormData.selectedContactId && addFormData.relationship);

  return (
    <div className="space-y-4">
      {/* Relationship Select */}
      <div className="space-y-2 p-1">
        <Label htmlFor="relationship">{t("Relationship")}</Label>
        <Select
          value={addFormData.relationship}
          onValueChange={(value) => setAddFormData((prev) => ({ ...prev, relationship: value }))}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("Select relationship type")} />
          </SelectTrigger>
          <SelectContent>
            {RELATIONSHIP_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Search Input */}
      <div className="space-y-2 p-1">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t("Search contacts...")}
            value={addFormData.contactSearchQuery}
            onChange={(e) => setAddFormData((prev) => ({ ...prev, contactSearchQuery: e.target.value }))}
            className="pl-9"
          />
        </div>
      </div>

      {/* Contact List */}
      <div className="space-y-2 p-1">
        {filteredAddContacts.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            {addFormData.contactSearchQuery ? t("No contacts found matching your search") : t("No contacts available")}
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto border rounded-md">
            <div className="space-y-1 p-2">
              {filteredAddContacts.map((c) => (
                <button
                  key={c.id}
                  onClick={() =>
                    setAddFormData((prev) => ({
                      ...prev,
                      selectedContactId: c.id === prev.selectedContactId ? "" : c.id,
                    }))
                  }
                  className={cn(
                    "w-full text-left p-3 rounded-md transition-colors hover:bg-muted",
                    addFormData.selectedContactId === c.id && "bg-primary text-primary-foreground hover:bg-primary/90"
                  )}
                >
                  <div className="font-medium">{formatName(c)}</div>
                  {c.email && (
                    <div
                      className={cn(
                        "text-sm",
                        addFormData.selectedContactId === c.id ? "text-primary-foreground/70" : "text-muted-foreground"
                      )}
                    >
                      {c.email}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 p-1">
        {!!onCancel && (
          <Button onClick={onCancel} variant="outline">
            {t("Cancel")}
          </Button>
        )}
        <Button onClick={handleAddRelatedContact} disabled={!canAddContact || updateRelationshipsMutation.isPending}>
          {t("Add Related Contact")}
        </Button>
      </div>
    </div>
  );
};
